<template>
  <div class="view-submesh" :class="{
       'plain-tiles': !realImages,
       empty: !submesh.empty,
       finished,
       }">
    <panZoom class="submesh-design" v-if="!submesh.empty"
             :options="panzoomOptions"
             @init="onPanzoomInit"
             @panend="panend = new Date()"
             ref="panzoomWrapper">
      <div ref="panzoomedRegion">
        <div class="tiles-container" ref="tilesContainer" :style="tilesContainerStyle">
          <Tile v-for="(tile, tileIdx) in submesh.tiles"
                :key="tileIdx"
                :tile-ref="tile.tileRef"
                :highlighted="highlighted === tile.tileRef"
                :plain="!realImages"
                :style="{ left: `${tile.x}px`, top: `${tile.y}px` }"
                :label="showTileCoords ? `${tile.tileX - minTileX},${tile.tileY - minTileY}` : false"
                :class="{ 'not-highlighted': highlighted !== null && highlighted !== tile.tileRef, highlighted: tile.tileRef && highlighted === tile.tileRef }"
                @touchstart.native="onTouchstart($event, tile)"
                @click.native="highlight(tile.tileRef)"
                @touchend.native="onTouchend($event, tile)"
                />
        </div>
      </div>
    </panZoom>
    <div class="submesh-design" v-if="submesh.empty">
      <div class="submesh-design-empty">
        La placa no tiene fichas.
      </div>
    </div>
    <div class="submesh-info">
      <div class="base-info d-flex flex-column justify-center align-center">
        <h1>
          <v-btn icon v-if="submeshIdxInBlock > 0" @click="viewSubmesh(-1)">
            <v-icon>mdi-arrow-left</v-icon>
          </v-btn>
          Placa {{submesh && submesh.label || 'vacía'}}
          <v-btn icon v-if="submeshIdxInBlock < block.submeshes.length - 1"  @click="viewSubmesh(1)">
            <v-icon>mdi-arrow-right</v-icon>
          </v-btn>
        </h1>
        <div v-if="!submesh.empty">Tamaño: {{design.metadata.htilesPerSubmesh}}x{{design.metadata.vtilesPerSubmesh}}</div>
        <div v-if="!submesh.empty">Fichas totales: {{totalTiles}}</div>
        <div class="d-flex justify-left flex-column">
          <v-checkbox
                v-model="realImages"
                label="Mostrar imágenes reales"
                dense
                ></v-checkbox>
          <v-checkbox
                v-model="showTileCoords"
                label="Mostrar coordenadas"
                dense
                class="mt-0"
                ></v-checkbox>
        </div>
      </div>
      <div class="legend d-flex flex-wrap justify-center align-content-start flex-grow-1 flex-shrink-1">
        <div v-for="entry in legend"
             :key="entry.tile"
             class="legend-tile d-flex justify-left align-center pa-3 rounded"
             :class="{ highlighted: highlighted === entry.tile }"
             @click="highlight(entry.tile)"
             >
          <Tile :tile-ref="entry.tile"
                :plain="!realImages"
                />
          <div class="tile-info pl-1 flex-grow-1">
            <div class="tile-ref">
              {{design.tileRefs[entry.tile].collection}}
              {{design.tileRefs[entry.tile].reference}}
            </div>
            <div class="count">x {{entry.count}}</div>
          </div>
        </div>
      </div>
      <div class="actions d-flex justify-space-around align-center">
        <v-btn small @click="fitZoom()">
          <v-icon class="mr-2">mdi-magnify-scan</v-icon>
          Reiniciar zoom
        </v-btn>
        <v-switch v-model="finished" label="Finalizada" class="mt-0"></v-switch>
      </div>
    </div>
    <v-snackbar v-model="submeshChangeError" color="red">
      No hay más placas no vacías en esa dirección.
    </v-snackbar>
  </div>
</template>

<script>
import Tile from '../components/Tile.vue';
import designService from '../services/design.service';
import { debounce } from 'lodash';

const RESIZE_DEBOUNCE_MS = 250;
let resizeTimeout = null;
const CLICK_DELTA = 6;

export default {
  components: {
    Tile
  },
  data() {
    return Object.assign(this.loadSubmeshIdx(this.$route.params.id), {
      realImages: true,
      showTileCoords: false,
      panzoomInstance: null,
      panzoomOptions: { 
        minZoom: 0.01, 
        maxZoom: 3,
        autocenter: true,
        bounds: true,
        smoothScroll: false,
      },
      panend: null,
      touchstartInfo: null,
      submeshChangeError: false,
    });    
  },
  created() {
    window.addEventListener("resize", this.onResize);
  },
  destroyed() {
    window.removeEventListener("resize", this.onResize);
  },
  methods: {
    highlight: debounce(function(tileRef) {
        if (this.panend && new Date() - this.panend < 60) {
          return;
        }
        this.highlighted = this.highlighted !== tileRef ? tileRef : null;
      }, 200),
    onPanzoomInit(instance) {
      this.panzoomInstance = instance;
      this.fitZoom();
    },
    onResize() {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(this.fitZoom, RESIZE_DEBOUNCE_MS);
    },
    fitZoom() {
      if (!this.panzoomInstance || !this.$refs.panzoomedRegion) {
        return;
      }
      const width = this.$refs.tilesContainer.clientWidth, 
              height = this.$refs.tilesContainer.clientHeight,
              viewportWidth = this.$refs.panzoomWrapper.$el.clientWidth,
              viewportHeight = this.$refs.panzoomWrapper.$el.clientHeight,
              minScale = Math.min(viewportWidth / width, viewportHeight / height);
      this.panzoomInstance.pause();
      this.panzoomInstance.setMinZoom(minScale);
      this.panzoomInstance.zoomAbs(0, 0, minScale);
      this.panzoomInstance.moveTo((viewportWidth - width * minScale) / 2, (viewportHeight - height * minScale) / 2);
      this.panzoomInstance.resume();
    },
    onTouchstart(event, tile) {
      if (event.touches.length > 1) {
        this.touchstartInfo = null;
      } else {
        this.touchstartInfo = {
          tile,
          x: event.touches[0].clientX,
          y: event.touches[0].clientY,
        };
      }
    },
    onTouchend(event, tile) {
      if (event.touches.length > 0) {
        return;
      }
      if (!this.touchstartInfo) {
        return;
      }
      if (this.touchstartInfo.tile === tile
              && Math.abs(event.changedTouches[0].clientX - this.touchstartInfo.x) <= CLICK_DELTA
              && Math.abs(event.changedTouches[0].clientY - this.touchstartInfo.y) <= CLICK_DELTA) {
        this.highlight(tile.tileRef);
      }
    },
    viewSubmesh(inc) {
      let newIdx = this.submeshIdxInBlock + inc;
      while (newIdx > 0 && newIdx < this.block.submeshes.length) {
        const realBlockSizeX = Math.min(this.block.sizeX, this.design.metadata.hsubmeshes - this.block.startSubmeshX),
                designSubmeshX = newIdx % realBlockSizeX + this.block.startSubmeshX,
                designSubmeshY = Math.floor(newIdx / realBlockSizeX) + this.block.startSubmeshY,
                newSubmeshIdx = designSubmeshY * this.design.metadata.hsubmeshes + designSubmeshX;
        if (this.block.emptySubmeshes.indexOf(newSubmeshIdx) === -1) {
          this.$router.replace({ name: 'view-submesh', params: { id: newSubmeshIdx } });
          return;
        } else {
          newIdx += inc;
        }
      }
      this.submeshChangeError = true;
    },
    loadSubmeshIdx(idx) {
      const design = designService.getCurrentDesign();
      const block = designService.getCurrentBlock();
      const rotated = design.metadata.geometryOptions.rotated;

      const realBlockSizeX = Math.min(block.sizeX, design.metadata.hsubmeshes - block.startSubmeshX),
              submeshXInBlock = idx % design.metadata.hsubmeshes - block.startSubmeshX,
              submeshYInBlock = Math.floor(idx / design.metadata.hsubmeshes) - block.startSubmeshY,
              submeshIdxInBlock = submeshYInBlock * realBlockSizeX + submeshXInBlock;

      const submesh = block.submeshes[submeshIdxInBlock] || {tiles: []};
      
      const tilesContainerStyle = {
        width: `${design.submeshWidth}px`,
        height: `${design.submeshHeight}px`,
      };

      let emptyTiles = 0, totalTiles = 0;
      const tileCounts = [];
      let minTileX = design.metadata.htilesPerSubmesh * design.metadata.hsubmeshes,
              minTileY = design.metadata.vtilesPerSubmesh * design.metadata.vsubmeshes;
      for (let tile of submesh.tiles) {
        minTileX = Math.min(tile.tileX, minTileX);
        minTileY = Math.min(tile.tileY, minTileY);
        const ref = tile.tileRef;
        if (ref !== null) {
          tileCounts[ref] = tileCounts[ref] ? tileCounts[ref] + 1 : 1;
          totalTiles++;
        } else {
          emptyTiles++;
        }
      }
      
      const legend = [];
      for (const [tile, count] of Object.entries(tileCounts)) {
        legend.push({
          tile: parseInt(tile), count
        });
      }
      legend.sort((a, b) => b.count - a.count || a.tile - b.tile);

      return {
        submesh,
        submeshIdx: idx,
        submeshIdxInBlock,
        tilesContainerStyle,
        rows: submesh.tiles.length,
        highlighted: null,
        totalTiles,
        tileCounts,
        emptyTiles,
        legend,
        design,
        block,
        minSubmeshIdx: block.startSubmeshY * design.metadata.hsubmeshes + block.startSubmeshX,
        maxSubmeshIdx: block.startSubmeshY * design.metadata.hsubmeshes + block.startSubmeshX + block.sizeX - 1,
        finished: designService.isSubmeshFinished(idx),
        rotated,
        minTileX,
        minTileY,
      };
    },
  },
  watch: {
    '$route.params.id': function(idx) {
      const newData = this.loadSubmeshIdx(idx);
      for (const prop in newData) {
        this[prop] = newData[prop];
      }
    },
    finished: function(val) {
      designService.markSubmeshFinished(this.submeshIdx, val);
    }
  },
}
</script>

<style>
.v-main {
  height: 100%;
}
.view-submesh {
  height: 100%;
  width: 100%;
  display: flex;
}
.portrait .view-submesh {
  flex-direction: column;
}
.submesh-design {
  flex: 1;
  overflow: hidden;
}
.submesh-design .tile-row {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-content: stretch;
}
.submesh-info {
  flex: 0 20%;
  display: flex;
  flex-flow: column;
  border-left: 1px solid black;
}
.portrait .submesh-info {
  flex-direction: row;
  border-left: none;
  border-top: 1px solid black;
}
.height-100 {
  height: 100%;
}
.tiles-container {
  position: relative;
}
.tile {
  cursor: pointer;
}
.tiles-container .tile {
  position: absolute;
}
.tile.tile-empty {
    cursor: not-allowed;
    z-index: -1;
}
.tile.tile-empty .tile-label {
  display: none;
}
.tile.not-highlighted {
    opacity: 0.4;
}
.not-alternating .plain-tiles .tile {
  border: 1px solid #666;
}
.not-alternating .tile.highlighted {
    border: 2px solid red;
}
.legend {
  overflow-y: scroll;
}
.legend-tile {
  cursor: pointer;
  line-height: 1.1;
}
.legend-tile.highlighted {
  background: chartreuse;
}
.legend-tile .tile {
    width: 2.5em;
    display: inline-block;
    vertical-align: middle;
}
.vue-pan-zoom-scene {
    outline: 0;
}
.v-input .v-messages {
    display: none;
}
.finished .submesh-design {
    opacity: 0.4;
}

</style>